import { useEffect, useRef } from 'react';
import {
  buildTeleconsultStaffChatHubConnection,
  getTeleconsultStaffChatHubUrl,
  type MeetPresenceUpdatedPayload,
} from '../realtime/teleconsultStaffChatHub';

const POLL_MS_FOREGROUND = 5000;
const POLL_MS_BACKGROUND = 15000;
const POLL_MS_SIGNALR_FALLBACK = 20_000;

type Options = {
  enabled?: boolean;
  /** Join doctor tele visit queue group (SignalR). Default true. */
  joinQueue?: boolean;
  /** Patient joined/left Aspen Meet — patch cards without a full queue reload. */
  onMeetPresenceUpdated?: (payload: MeetPresenceUpdatedPayload) => void;
};

/**
 * Keeps tele visit queue lists fresh — polls like the patient waiting room and
 * refreshes immediately on SignalR TeleVisitQueueUpdated (pick/unpick, registration, payment).
 */
export function useTeleVisitQueueRealtime(
  onRefresh: () => void | Promise<void>,
  options: Options = {},
) {
  const enabled = options.enabled !== false;
  const joinQueue = options.joinQueue !== false;
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const onMeetPresenceUpdatedRef = useRef(options.onMeetPresenceUpdated);
  onMeetPresenceUpdatedRef.current = options.onMeetPresenceUpdated;
  const hubConnectedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const refresh = () => void Promise.resolve(onRefreshRef.current());

    let intervalId: number | undefined;
    const armInterval = () => {
      if (intervalId != null) window.clearInterval(intervalId);
      const ms = hubConnectedRef.current
        ? POLL_MS_SIGNALR_FALLBACK
        : document.visibilityState === 'visible'
          ? POLL_MS_FOREGROUND
          : POLL_MS_BACKGROUND;
      intervalId = window.setInterval(refresh, ms);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
      armInterval();
    };

    void refresh();
    armInterval();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (intervalId != null) window.clearInterval(intervalId);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !joinQueue) return;

    const hubUrl = getTeleconsultStaffChatHubUrl();
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('accessToken') : null;
    const conn = buildTeleconsultStaffChatHubConnection({
      hubUrl,
      accessToken: token,
      onStaffChatUpdated: () => {},
      onTeleVisitQueueUpdated: () => {
        void Promise.resolve(onRefreshRef.current());
      },
      onMeetPresenceUpdated: (payload) => {
        onMeetPresenceUpdatedRef.current?.(payload);
      },
    });

    const join = async () => {
      try {
        await conn.invoke('JoinTeleVisitQueue');
      } catch {
        /* hub may still be negotiating */
      }
    };

    conn.onreconnected(() => {
      void join();
    });

    let cancelled = false;

    void (async () => {
      try {
        await conn.start();
        if (cancelled) return;
        hubConnectedRef.current = true;
        await join();
      } catch (e) {
        hubConnectedRef.current = false;
        console.warn('[SignalR tele visit queue]', e);
      }
    })();

    return () => {
      cancelled = true;
      hubConnectedRef.current = false;
      void conn.stop();
    };
  }, [enabled, joinQueue]);
}
